import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AlertTriangle, CheckCircle2, ExternalLink, Grip, ImageOff, Search, Trash2 } from "lucide-react";
import { useCallback, useMemo, useState, type ReactNode } from "react";
import { Link } from "react-router";

import { useOrganization } from "@/auth/organization";
import { UpgradeModal } from "@/components/billing/UpgradeModal";
import { RestoreSiteButton } from "@/components/sites/RestoreSiteButton";
import { DeletionCountdown } from "@/components/status";
import { GetHelpLink } from "@/components/support/GetHelpLink";
import {
  Badge,
  Button,
  CopyLinkButton,
  ErrorAlert,
  ErrorState,
  FullPageSpinner,
  Modal,
  cx,
  useToast,
  type Requirement,
} from "@/components/ui";
import { portal } from "@/lib/api";
import { config } from "@/lib/config";
import { canBuildWithAi } from "@/lib/entitlements";
import { formatDate, siteHref, timeAgo } from "@/lib/format";
import { primarySite, siteKeys, useOverview } from "@/lib/queries";
import { TEMPLATES } from "@/lib/templates";
import type { CustomDomain, Plan, Site } from "@/lib/types";
import { text, useUrlText, useUrlView } from "@/lib/url-state";

import { frameable, LiveSiteFrame } from "./LiveSiteFrame";

/** "My Bakery!" → "my-bakery" (the runtime's slug rules: 3–63 of a-z, 0-9, hyphen). */
export function slugify(name: string): string {
  return name
    .normalize("NFKD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 63)
    .replace(/-+$/g, "");
}

const builderSiteUrl = (siteId: string, template?: string): string =>
  `${config.builderUrl}/sites/${encodeURIComponent(siteId)}${template ? `?template=${encodeURIComponent(template)}` : ""}`;

/**
 * The workspace's one website (Figma "Website — Pages Empty State" / "Website — Overview (Live)").
 * Workspaces created before the one-website rule may hold several; the live one leads.
 */
export function WebsitesPage() {
  const { orgId } = useOrganization();
  const sites = useQuery({ queryKey: ["sites", orgId], queryFn: () => portal.sites(orgId) });
  const overview = useOverview();

  if (sites.isPending || overview.isPending) return <FullPageSpinner />;
  if (sites.isError) return <ErrorState error={sites.error} onRetry={() => void sites.refetch()} />;
  if (overview.isError) return <ErrorState error={overview.error} onRetry={() => void overview.refetch()} />;

  const site = primarySite(sites.data.sites);
  const plan = overview.data.account.plan;
  if (site?.status === "pending_deletion") return <PendingDeletionWebsite site={site} />;
  return site?.live_deployment_id ? (
    <PublishedWebsite site={site} plan={plan} />
  ) : (
    <EmptyWebsite site={site} plan={plan} />
  );
}

/** Open the builder on the workspace's website (created on first use), or explain the upgrade. */
function useBuilder(site: Site | undefined, plan: Plan) {
  const { orgId, current } = useOrganization();
  const qc = useQueryClient();
  const [upgradeOpen, setUpgradeOpen] = useState(false);
  const open = useMutation({
    mutationFn: async (template?: string) => {
      const target = site ?? (await portal.createSite(orgId, { name: current.organization.name }));
      return builderSiteUrl(target.id, template);
    },
    onSuccess: async (url) => {
      await qc.invalidateQueries({ queryKey: ["sites", orgId] });
      window.location.assign(url);
    },
  });
  return {
    start: (template?: string) => (canBuildWithAi(plan) ? open.mutate(template) : setUpgradeOpen(true)),
    pending: open.isPending,
    error: open.error,
    upgrade: <UpgradeModal open={upgradeOpen} onClose={() => setUpgradeOpen(false)} current={plan} />,
  };
}

function Heading({
  icon,
  tone,
  title,
  subtitle,
}: {
  icon: ReactNode;
  tone: "brand" | "success";
  title: string;
  subtitle: string;
}) {
  return (
    <div className="flex items-center gap-3.5">
      <div
        className={cx(
          "flex size-10 shrink-0 items-center justify-center rounded-[11px]",
          tone === "success" ? "bg-[#eef8f3] text-[#12a97e]" : "bg-brand-50 text-brand-500",
        )}
      >
        {icon}
      </div>
      <div className="flex min-w-0 flex-col gap-[5px]">
        <h1 className="text-2xl font-semibold text-fg">{title}</h1>
        <p className="text-sm text-fg-subtle">{subtitle}</p>
      </div>
    </div>
  );
}

const panel = "rounded-[18px] border border-border bg-surface shadow-[0_6px_10px_rgba(26,20,18,0.08)]";
const coralButton =
  "inline-flex items-center justify-center gap-2 rounded-[10px] bg-brand-500 px-4 py-2.5 text-sm font-semibold text-white shadow-[0_3px_5px_rgba(26,20,18,0.18)] hover:bg-brand-600 disabled:cursor-not-allowed disabled:opacity-60";

// ── scheduled for deletion ─────────────────────────────────────────────────
function PendingDeletionWebsite({ site }: { site: Site }) {
  const { canOn } = useOrganization();
  return (
    <div className="flex flex-col gap-6">
      <Heading
        tone="brand"
        icon={<Trash2 className="size-6" aria-hidden />}
        title="Your website is scheduled for deletion"
        subtitle={
          site.purge_at
            ? `${site.name} is offline and will be permanently deleted on ${formatDate(site.purge_at)}`
            : `${site.name} is offline`
        }
      />
      <section
        className={cx(panel, "flex flex-col items-center gap-[18px] rounded-[24px] px-6 py-14 text-center")}
      >
        <h2 className="text-xl font-semibold text-fg">You can still get it back</h2>
        <p className="max-w-[460px] text-sm text-fg-subtle">
          Restore it before the date above and it goes live again with its pages and domains. After that its
          files, database and domains are permanently deleted.
        </p>
        <DeletionCountdown site={site} />
        {canOn("websites:delete", "site", site.id) ? (
          <RestoreSiteButton site={site} variant="primary" size="md" />
        ) : (
          <p className="text-sm text-fg-subtle">Ask a workspace owner to restore the website.</p>
        )}
      </section>
    </div>
  );
}

// ── not published yet ──────────────────────────────────────────────────────
function EmptyWebsite({ site, plan }: { site: Site | undefined; plan: Plan }) {
  const builder = useBuilder(site, plan);
  const requires: Requirement = site
    ? { permission: "websites:publish", on: ["site", site.id] }
    : "websites:create";
  return (
    <div className="flex flex-col gap-6">
      <Heading
        tone="brand"
        icon={<AlertTriangle className="size-6" aria-hidden />}
        title="Your website doesn't have any pages yet"
        subtitle="Ask Weavex to build your first page, or start from a blank one"
      />
      <ErrorAlert error={builder.error} />

      <section
        className={cx(panel, "flex flex-col items-center gap-[18px] rounded-[24px] px-6 py-14 text-center")}
      >
        <div className="flex size-[72px] items-center justify-center rounded-[20px] bg-brand-50 text-brand-500">
          <Grip className="size-[43px]" strokeWidth={1.5} aria-hidden />
        </div>
        <h2 className="text-xl font-semibold text-fg">Nothing published yet</h2>
        <p className="max-w-[460px] text-sm text-fg-subtle">
          Tell Weavex about your business and it&apos;ll generate a homepage and starter pages you can preview
          before anything goes live.
        </p>
        <Button
          unstyled
          className={coralButton}
          requires={requires}
          loading={builder.pending}
          onClick={() => builder.start()}
        >
          {builder.pending ? "Opening…" : "Build with Weavex AI"}
        </Button>
      </section>

      <TemplateGallery requires={requires} pending={builder.pending} onUse={(id) => builder.start(id)} />
      {builder.upgrade}
    </div>
  );
}

/**
 * The template search kept in the URL (PHP-3341): `?q=` (default none). Templates keep the catalogue's
 * order (lib/templates.ts); there's no sort.
 */
const GALLERY_VIEW = { q: text("q") };

function TemplateGallery({
  requires,
  pending,
  onUse,
}: {
  requires: Requirement;
  pending: boolean;
  onUse: (templateId: string) => void;
}) {
  const [view, setView] = useUrlView(GALLERY_VIEW);
  const commitQuery = useCallback((q: string) => setView({ q }, { replace: true }), [setView]);
  const [query, setQuery] = useUrlText(view.q, commitQuery);
  const shown = useMemo(() => {
    const q = query.trim().toLowerCase();
    return TEMPLATES.filter(
      (t) => !q || `${t.name} ${t.category} ${t.description}`.toLowerCase().includes(q),
    );
  }, [query]);
  return (
    <section className={cx(panel, "flex flex-col gap-4 p-[22px]")}>
      <div className="flex flex-wrap items-center gap-2">
        <h2 className="mr-auto text-xl font-semibold text-fg">Or start from a template</h2>
        <CopyLinkButton />
      </div>
      <label className="flex h-10 items-center gap-2 rounded-full border border-border bg-surface px-[18px]">
        <Search className="size-4 text-fg-subtle" aria-hidden />
        <input
          type="search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search templates in different niches, design style and more"
          aria-label="Search templates"
          className="min-w-0 flex-1 bg-transparent text-xs text-fg outline-none placeholder:text-[#b3a8a3]"
        />
      </label>
      {shown.length === 0 ? (
        <p className="py-8 text-center text-sm text-fg-subtle">No templates match “{query.trim()}”.</p>
      ) : (
        <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {shown.map((t) => (
            <Button
              key={t.id}
              unstyled
              requires={requires}
              loading={pending}
              onClick={() => onUse(t.id)}
              className="group flex flex-col overflow-hidden rounded-[13px] text-left shadow-[3px_3px_36px_rgba(12,4,3,0.02)] ring-1 ring-border transition-shadow hover:shadow-md disabled:cursor-not-allowed disabled:opacity-60"
            >
              <div className={cx("flex aspect-[4/3] flex-col justify-between bg-gradient-to-br p-5", t.tint)}>
                <t.icon className="size-7 text-fg/70" aria-hidden />
                <p className="max-w-[80%] text-sm text-fg/75">{t.description}</p>
              </div>
              <div className="flex items-center gap-2.5 bg-brand-50 px-[18px] py-[13px]">
                <div className="min-w-0 flex-1">
                  <p className="truncate text-[13px] font-semibold text-[#2e0d05]">{t.name}</p>
                  <p className="truncate text-[10.5px] text-fg-subtle">{t.category}</p>
                </div>
                <span className="shrink-0 text-[11px] font-semibold text-brand-500 group-hover:underline">
                  Use this template →
                </span>
              </div>
            </Button>
          ))}
        </div>
      )}
    </section>
  );
}

// ── published ──────────────────────────────────────────────────────────────
function PublishedWebsite({ site, plan }: { site: Site; plan: Plan }) {
  const { orgId } = useOrganization();
  const builder = useBuilder(site, plan);
  const publish: Requirement = { permission: "websites:publish", on: ["site", site.id] };
  const deployments = useQuery({
    queryKey: siteKeys.deployments(orgId, site.id),
    queryFn: () => portal.deployments(site.id),
  });
  const domains = useQuery({
    queryKey: siteKeys.domains(orgId, site.id),
    queryFn: () => portal.domains(site.id),
  });
  const live = deployments.data?.deployments.find((d) => d.id === site.live_deployment_id);
  const domain = domains.data?.domains.find((d) => d.status === "active");
  const address = domain?.hostname ?? site.hostname;
  const served = site.status === "active";
  const unpublished = site.offline_reason === "unpublished";

  const subtitle = [live && `Published ${timeAgo(live.finished_at ?? live.created_at)}`, address]
    .filter(Boolean)
    .join(" · ");
  return (
    <div className="flex flex-col gap-6">
      {served ? (
        <Heading
          tone="success"
          icon={<CheckCircle2 className="size-6" aria-hidden />}
          title="Your website is live and published"
          subtitle={subtitle}
        />
      ) : (
        <>
          <Heading
            tone="brand"
            icon={<AlertTriangle className="size-6" aria-hidden />}
            title={
              site.abuse_case_reference
                ? `Unpublished following an abuse report ${site.abuse_case_reference}`
                : unpublished
                  ? "Your website is unpublished"
                  : "Your website is offline"
            }
            subtitle={
              site.abuse_case_reference
                ? `Visitors see "This site is unavailable." You can't publish it again while the case is open. Contact support quoting ${site.abuse_case_reference}.`
                : unpublished
                  ? "Visitors see a temporary placeholder. Publish it again whenever you're ready."
                  : "CortexGrip has taken this website offline. Contact support to restore it."
            }
          />
          {!unpublished && (
            <div>
              <GetHelpLink
                context={{ resourceType: "website", resourceId: address, reason: "Website taken offline" }}
              />
            </div>
          )}
        </>
      )}
      <ErrorAlert error={builder.error} />

      <div className="flex flex-col gap-4 lg:flex-row lg:items-start">
        <section className={cx(panel, "flex min-w-0 flex-1 flex-col gap-4 rounded-2xl p-[22px]")}>
          <div className="flex items-start gap-4">
            <div className="min-w-0">
              <h2 className="text-lg font-semibold text-fg">Preview</h2>
              <p className="text-sm text-fg-subtle">
                {served ? "What visitors see right now at your live domain" : "Your latest published version"}
              </p>
            </div>
            <span className="ml-auto">
              {served ? (
                <Badge tone="success">Live</Badge>
              ) : (
                <Badge>{unpublished ? "Unpublished" : "Offline"}</Badge>
              )}
            </span>
          </div>
          <div className="relative h-[444px] overflow-hidden rounded-xl border border-border bg-muted">
            <SitePreview site={site} />
          </div>
          <div className="flex flex-wrap gap-2.5">
            <Button
              unstyled
              className={cx(coralButton, "rounded-[9px] px-3.5 py-[9px]")}
              requires={publish}
              loading={builder.pending}
              onClick={() => builder.start()}
            >
              {builder.pending ? "Opening…" : "Edit with Weavex AI"}
            </Button>
            {served && (
              <a
                href={siteHref(address, site.link_scheme)}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-1.5 rounded-[9px] border border-border bg-surface px-3.5 py-[9px] text-sm font-semibold text-fg hover:bg-muted"
              >
                Visit live site <ExternalLink className="size-3.5" aria-hidden />
              </a>
            )}
          </div>
        </section>

        <div className="flex w-full shrink-0 flex-col gap-5 lg:w-[344px]">
          <WebsiteHealth site={site} domains={domains.data?.domains} />
          {(unpublished || served) && <PublishSwitch site={site} requires={publish} />}
        </div>
      </div>
      {builder.upgrade}
    </div>
  );
}

function SitePreview({ site }: { site: Site }) {
  const [failed, setFailed] = useState(false);
  if (site.thumbnail_url && !failed)
    return (
      <img
        src={site.thumbnail_url}
        alt={`Preview of ${site.name}`}
        className="size-full object-cover object-top"
        onError={() => setFailed(true)}
      />
    );
  // PHP-4126: no screenshot (the screenshotter is off on small hosts), so show the live site itself.
  if (site.status === "active" && site.live_deployment_id) return <LiveSitePreview site={site} />;
  return (
    <NoPreview>
      {site.status === "active" ? "Nothing is published on this website yet." : "No preview while offline."}
    </NoPreview>
  );
}

/** Preview links work for 30 minutes; ask for a fresh one well before that. */
const PREVIEW_LINK_STALE_MS = 20 * 60 * 1000;

function LiveSitePreview({ site }: { site: Site }) {
  const link = useQuery({
    queryKey: ["site-preview", site.id, site.live_deployment_id],
    queryFn: () => portal.sitePreview(site.id),
    staleTime: PREVIEW_LINK_STALE_MS,
    gcTime: PREVIEW_LINK_STALE_MS,
    retry: false,
  });
  if (link.isError || (link.data && !frameable(link.data.url)))
    return <NoPreview>The preview can’t be shown right now. Open your website to see it.</NoPreview>;
  if (!link.data)
    return (
      <div role="status" className="flex size-full items-center justify-center text-sm text-fg-muted">
        Loading the preview…
      </div>
    );
  return (
    <div role="img" aria-label={`Preview of ${site.name}`} className="size-full">
      <LiveSiteFrame url={link.data.url} title={`Preview of ${site.name}`} fill />
    </div>
  );
}

function NoPreview({ children }: { children: string }) {
  return (
    <div className="flex size-full flex-col items-center justify-center gap-2 text-fg-muted">
      <ImageOff className="size-6" aria-hidden />
      <p className="text-sm">{children}</p>
    </div>
  );
}

function HealthRow({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex items-center gap-2.5">
      <p className="text-sm font-medium text-fg-muted">{label}</p>
      <span className="ml-auto">{children}</span>
    </div>
  );
}

function WebsiteHealth({ site, domains }: { site: Site; domains: CustomDomain[] | undefined }) {
  const served = site.status === "active";
  const connected = domains?.some((d) => d.status === "active");
  const pending = domains?.some((d) => d.status === "pending_verification");
  return (
    <section className={cx(panel, "flex flex-col gap-4 rounded-2xl p-[22px]")}>
      <h2 className="text-lg font-semibold text-fg">Website health</h2>
      <HealthRow label="Custom domain">
        {domains === undefined ? (
          <Badge>Checking…</Badge>
        ) : connected ? (
          <Badge tone="success">Connected</Badge>
        ) : pending ? (
          <Badge tone="warning">Pending DNS</Badge>
        ) : (
          <Link to="/domains" className="text-xs font-semibold text-brand-600 hover:underline">
            Connect
          </Link>
        )}
      </HealthRow>
      <HealthRow label="SSL certificate">
        {!served ? (
          <Badge>Not serving</Badge>
        ) : site.link_scheme === "http" ? (
          <Badge tone="warning">Not in use (http)</Badge>
        ) : (
          <Badge tone="success">Active</Badge>
        )}
      </HealthRow>
      <HealthRow label="Deployments">
        <Link
          to={`/websites/${site.id}?tab=deployments`}
          className="text-xs font-semibold text-brand-600 hover:underline"
        >
          History &amp; rollback
        </Link>
      </HealthRow>
    </section>
  );
}

function PublishSwitch({ site, requires }: { site: Site; requires: Requirement }) {
  const { orgId } = useOrganization();
  const qc = useQueryClient();
  const [confirming, setConfirming] = useState(false);
  const toast = useToast();
  const toggle = useMutation({
    mutationFn: () =>
      site.status === "active" ? portal.unpublishSite(site.id) : portal.republishSite(site.id),
    onSuccess: async (updated) => {
      setConfirming(false);
      toast.show({
        tone: "success",
        title: updated.status === "active" ? "Website published" : "Website unpublished",
      });
      qc.setQueryData(siteKeys.site(orgId, site.id), updated);
      await qc.invalidateQueries({ queryKey: ["sites", orgId] });
      await qc.invalidateQueries({ queryKey: ["overview", orgId] });
    },
  });

  if (site.status !== "active")
    return (
      <section className={cx(panel, "flex flex-col gap-3 rounded-[14px] p-4")}>
        <h2 className="text-base font-semibold text-fg">Publish website</h2>
        <p className="text-sm text-fg-muted">Serve your latest published version to visitors again.</p>
        <ErrorAlert error={toggle.error} />
        <Button
          className="self-start"
          requires={requires}
          loading={toggle.isPending}
          onClick={() => toggle.mutate()}
        >
          Publish again
        </Button>
      </section>
    );

  return (
    <section className="flex flex-col gap-3 rounded-[14px] border border-[#ffc4bb] bg-[#fff1ee] p-4 shadow-[0_6px_10px_rgba(26,20,18,0.08)]">
      <h2 className="text-base font-semibold text-fg">Unpublish website</h2>
      <p className="text-sm text-[#766e6a]">
        Visitors will see a temporary placeholder instead of your site. This does not delete anything.
      </p>
      <Button
        unstyled
        requires={requires}
        onClick={() => setConfirming(true)}
        className="self-start rounded-[9px] bg-[#d6342f] px-3.5 py-[9px] text-sm font-semibold text-white shadow-[0_3px_5px_rgba(26,20,18,0.18)] hover:bg-[#bd2b26]"
      >
        Unpublish
      </Button>
      <Modal
        open={confirming}
        onClose={() => setConfirming(false)}
        title="Unpublish website?"
        footer={
          <>
            <Button variant="secondary" onClick={() => setConfirming(false)}>
              Cancel
            </Button>
            <Button variant="danger" loading={toggle.isPending} onClick={() => toggle.mutate()}>
              Unpublish
            </Button>
          </>
        }
      >
        <ErrorAlert error={toggle.error} />
        <p className="text-sm text-fg-muted">
          <strong className="text-fg">{site.hostname}</strong> and any connected domains will show a
          placeholder until you publish again. Your pages, deployments and domains are kept.
        </p>
      </Modal>
    </section>
  );
}
